import { CARRIER, PARCEL_TYPE } from '~/basket/model/constants'
import {
  ServiceExpressEconomyQueryDocument,
  ServiceExpressQueryDocument,
  ServiceParcelQueryDocument,
  ServiceQuery,
  ServiceQueryDocument,
} from '~/gql'

import { apolloClient } from '~/apollo'
import { convertDateToDateString } from '~/basket/view/util/dateTime'
import { isExpressOrExpressEconomy } from '../utils/carrier'
import { isFeatureEnabled } from '~/config'

export const fetchCapabilities = (
  originCountry: string,
  destinationCountry: string,
  carrierKey: string | null,
  parcelTypeKey: string | null,
  toPostalCode: string | undefined = undefined,
  fromPostalCode: string | undefined = undefined,
  productKey: string | undefined = undefined,
  city: string | undefined = undefined
): Promise<ServiceQuery> => {
  const handOffDate: string = convertDateToDateString(new Date())
  const isExpressEconomyEnabled = isFeatureEnabled('expressEconomy')

  const query = () => {
    if (!carrierKey) {
      return ServiceQueryDocument
    }
    switch (carrierKey) {
      case CARRIER.EXPRESS:
        return ServiceExpressQueryDocument
      case CARRIER.EXPRESS_ECONOMY:
        return ServiceExpressEconomyQueryDocument
      default:
        return ServiceParcelQueryDocument
    }
  }

  const customsVariables = {
    customsDestinationInput: {
      fromCountryCode: originCountry,
      fromPostalCode,
      toCountryCode: destinationCountry,
      toPostalCode,
      productKey,
    },
  }

  const parcelVariables = {
    ...customsVariables,
    parcelParcelTypesInput: {
      fromBusiness: false,
      fromCountry: originCountry,
      fromPostalCode,
      toPostalCode,
      toCountry: destinationCountry,
      carrier: CARRIER.PARCEL,
    },
    parcelCapabilitiesInput: {
      fromBusiness: false,
      fromCountry: originCountry,
      fromPostalCode,
      carrier: CARRIER.PARCEL,
      toCountry: destinationCountry,
      toPostalCode,
      toCity: city,
      parcelType: parcelTypeKey,
      referenceTimeStamp: handOffDate,
    },
    parcelDestinationCountriesInput: {
      carrier: CARRIER.PARCEL,
      fromCountry: originCountry,
      fromBusiness: false,
    },
    parcelHandOffDatesInput: {
      fromCountry: originCountry,
      toCountry: destinationCountry,
      parcelType: parcelTypeKey ?? '',
      toPostalCode,
      toCity: city,
      carrier: CARRIER.PARCEL,
      shipmentDate: handOffDate,
    },
    parcelTimeWindowsInput: {
      fromCountry: originCountry,
      fromPostalCode,
    },
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const expressVariables = (carrierKey: string) => {
    const parcelTypesInput = {
      fromBusiness: false,
      fromCountry: originCountry,
      toPostalCode,
      toCountry: destinationCountry,
      carrier: carrierKey,
    }

    const capabilitiesInput = {
      fromBusiness: false,
      fromCountry: originCountry,
      carrier: carrierKey,
      toCountry: destinationCountry,
      toPostalCode,
      toCity: city,
      parcelType: parcelTypeKey || PARCEL_TYPE.SMALL,
      referenceTimeStamp: handOffDate,
    }

    const destinationCountriesInput = {
      carrier: carrierKey,
      fromCountry: originCountry,
      fromBusiness: false,
    }

    const handOffDatesInput = {
      fromCountry: originCountry,
      toCountry: destinationCountry,
      parcelType: 'SMALL', // TODO: This doesn't make sense.. SMALL is hardcoded in the old implementation so I keep it for now
      toPostalCode,
      toCity: city,
      carrier: carrierKey,
      shipmentDate: handOffDate,
    }

    const timeWindowsInput = {
      fromCountry: originCountry,
      fromPostalCode,
    }

    const variables =
      carrierKey === CARRIER.EXPRESS
        ? {
            ...customsVariables,
            expressParcelTypesInput: parcelTypesInput,
            expressCapabilitiesInput: capabilitiesInput,
            expressDestinationCountriesInput: destinationCountriesInput,
            expressHandOffDatesInput: handOffDatesInput,
            expressTimeWindowsInput: timeWindowsInput,
          }
        : {
            ...customsVariables,
            expressEconomyParcelTypesInput: parcelTypesInput,
            expressEconomyCapabilitiesInput: capabilitiesInput,
            expressEconomyDestinationCountriesInput: destinationCountriesInput,
            expressEconomyHandOffDatesInput: handOffDatesInput,
            expressEconomyTimeWindowsInput: timeWindowsInput,
          }

    return variables
  }

  const variables = () => {
    if (!carrierKey) {
      return {
        ...parcelVariables,
        ...expressVariables(CARRIER.EXPRESS),
        ...(isExpressEconomyEnabled ? expressVariables(CARRIER.EXPRESS_ECONOMY) : {}),
      }
    }
    return isExpressOrExpressEconomy(carrierKey) ? expressVariables(carrierKey) : parcelVariables
  }

  return apolloClient
    .query({
      query: query(),
      fetchPolicy: 'no-cache',
      variables: variables(),
    })
    .then((result) => result.data)
}
